/**
 * Le niveau est toujours stocké sur une échelle canonique 0-100.
 * Le mode d'affichage (étoiles ou score) n'est qu'une vue: basculer d'un mode
 * à l'autre ne modifie plus les données, ce qui supprime toute désynchro.
 */
export type RatingMode = 'STARS' | 'SCORE';

export interface Player {
  id: string;
  name: string;
  /** 0-100, échelle canonique. */
  rating: number;
  /** Présent au prochain match. Seuls les présents entrent dans les équipes. */
  active: boolean;
  /**
   * Présent mais volontairement mis sur le banc. Le choix est toujours manuel:
   * l'application ne désigne jamais un remplaçant à la place de l'utilisateur.
   */
  benched: boolean;
}

export interface TeamMetrics {
  teamAScore: number;
  teamBScore: number;
  teamAAvg: number;
  teamBAvg: number;
  diff: number;
  /** Part de la force totale détenue par l'équipe A, 0-100. */
  balance: number;
}

export interface TeamComposition {
  teamA: Player[];
  teamB: Player[];
  /** Présents laissés de côté. Vide quand tout le monde joue. */
  bench: Player[];
  metrics: TeamMetrics;
}

/**
 * Nombre de remplaçants qu'il reste à désigner pour que les deux équipes
 * soient complètes et de taille égale.
 *
 * `teamSize` null (mode auto) n'impose aucun plafond: l'effectif entier est
 * réparti, quitte à produire des équipes inégales d'un joueur.
 */
export function benchSurplus(playingCount: number, teamSize: number | null): number {
  if (teamSize === null) return 0;
  const effective = Math.min(teamSize, Math.floor(playingCount / 2));
  return playingCount - 2 * effective;
}

/** Étoiles (1-5) -> score canonique (0-100). */
export function starsToRating(stars: number): number {
  return Math.min(100, Math.max(0, Math.round(stars) * 20));
}

/** Score canonique (0-100) -> étoiles (1-5). */
export function ratingToStars(rating: number): number {
  return Math.min(5, Math.max(1, Math.round(rating / 20)));
}
